/**
 * Fields the server can filter and sort the request grid by: the properties of DownstreamContractRequestGridRow.
 * A filter or sort on any other field makes the server throw, and the grid then fails to load.
 */
export const ServerGridFields = [
    'Id', 'Company', 'OriginalSubmissionDate', 'ContractType', 'ProjectName', 'ProjectManagerDisplay',
    'SubcontractorVendorName', 'ShortDescription', 'SharedServicesComments', 'Status', 'StatusText', 'StatusDate'
];

const isServerField = (field) => ServerGridFields.includes(field);

/**
 * Copy of a saved filter with every condition on a non-server field removed, at any depth. A group left with no
 * conditions is removed too. Returns null when nothing is left.
 */
const keepServerFilters = (filter) => {
    if (!filter) return null;

    if (Array.isArray(filter) || Array.isArray(filter.filters)) {
        const kept = (Array.isArray(filter) ? filter : filter.filters).map(keepServerFilters).filter(Boolean);
        if (kept.length === 0) return null;
        return Array.isArray(filter) ? kept : {...filter, filters: kept};
    }

    return isServerField(filter.field) ? filter : null;
};

/**
 * Initial filter and sort for the request grid's data source, from the state saved in localStorage.
 * Passing them in as the data source's initial state (rather than applying them after the grid is created) means the
 * first server read already uses them, so the page loads with one request instead of three.
 * Saved state from before the grid moved to server-side filtering can refer to fields the server cannot handle, such
 * as the Branch column, so those conditions are dropped.
 * @param {{filters?: object, sort?: object[]} | null} cachedFilters The saved grid state, or null when nothing is saved.
 * @returns {{filter: object | object[], sort: object[]}} The data source's initial filter and sort.
 */
export const getInitialGridState = (cachedFilters) => ({
    filter: keepServerFilters(cachedFilters?.filters) ?? [],
    sort: (Array.isArray(cachedFilters?.sort) ? cachedFilters.sort : []).filter(sort => isServerField(sort.field))
});

/**
 * Cell markup for a date column. The server sends null for a date that has never been set, which shows as a dash.
 * @param {string | null | undefined} value The date as sent by the server.
 * @returns {string} The cell markup.
 */
export const formatGridDateTime = (value) => {
    if (!value) return '<span>-</span>';
    const formattedDate = kendo.toString(new Date(value), "MM/dd/yyyy hh:mm tt");
    return `<span>${formattedDate}</span>`;
};
